"use client";
import { useState } from "react";
import * as motion from "motion/react-client";
import { TEducationConfig } from "@/app/config";

// Mock config for demonstration

const StudentRegistrationSection = ({
  config,
}: {
  config: TEducationConfig["StudentRegistrationSection"];
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  // Form state
  const [formData, setFormData] = useState({
    schoolName: "",
    email: "",
    phone: "",
    address: "",
    comment: "",
  });

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const accessKey = process.env.NEXT_PUBLIC_ACCESS_KEY;

      if (!accessKey) {
        throw new Error("Access key not found");
      }

      const payload = {
        public_access_key: accessKey,
        content: {
          "School/InstituteName": formData.schoolName,
          Phone: formData.phone,
          Email: formData.email,
          Address: formData.address,
          Comments: formData.comment,
        },
      };

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_ACCESS_API}/collection/data/public`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          schoolName: "",
          email: "",
          phone: "",
          address: "",
          comment: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <div
      id="students"
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center py-8 px-4"
    >
      {/* Background Image with Yellow Overlay */}
      <motion.div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${config.backgroundImage.imageUrl})`,
        }}
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
      >
        <motion.div
          className="absolute inset-0 bg-yellow-400 opacity-80"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.8 }}
          transition={{ duration: 1, delay: 0.3 }}
        />
      </motion.div>

      {/* Content Container */}
      <motion.div
        className="relative z-10 w-full max-w-7xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
          {/* Left Side - Content */}
          <motion.div
            className="flex-1 flex flex-col justify-center text-center lg:text-left px-4 lg:px-8"
            variants={{
              hidden: {
                opacity: 0,
                x: -50,
              },
              visible: {
                opacity: 1,
                x: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut",
                },
              },
            }}
          >
            {/* Student Tag */}
            <motion.div
              className="mb-6 lg:mb-8"
              variants={{
                hidden: {
                  opacity: 0,
                  y: 30,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
            >
              <motion.span
                className="underline-style-white text-black font-medium text-base"
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
                transition={{ duration: 0.2 }}
              >
                STUDENT
              </motion.span>
            </motion.div>

            {/* Title */}
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-black mb-4 lg:mb-6 leading-tight"
              variants={{
                hidden: {
                  opacity: 0,
                  y: 30,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{ once: true }}
            >
              {config.title.split(" ").map((word, index) => (
                <motion.span
                  key={index}
                  className="inline-block mr-2"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.1,
                    ease: "easeOut",
                  }}
                >
                  {word}
                </motion.span>
              ))}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              className="text-black text-base sm:text-lg opacity-80 max-w-md mx-auto lg:mx-0"
              variants={{
                hidden: {
                  opacity: 0,
                  y: 30,
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut",
                  },
                },
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              {config.description}
            </motion.p>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            className="flex-1 flex items-center justify-center w-full max-w-md lg:max-w-lg"
            variants={{
              hidden: {
                opacity: 0,
                x: 50,
              },
              visible: {
                opacity: 1,
                x: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut",
                },
              },
            }}
          >
            <div className="w-full">
              {/* Form Container */}
              <motion.div
                className="w-full p-6 sm:p-8 rounded-md shadow-lg"
                style={{ backgroundColor: "#043F5E" }}
                initial={{
                  opacity: 0,
                  y: 50,
                  boxShadow: "0 0 0 rgba(0,0,0,0)",
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                }}
                transition={{
                  duration: 0.8,
                  delay: 0.4,
                  ease: "easeOut",
                }}
                whileHover={{
                  boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
                  transition: { duration: 0.3 },
                }}
              >
                {/* Success/Error Messages */}
                <motion.div className="mb-4">
                  {submitStatus === "success" && (
                    <motion.div
                      className="p-3 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      Registration successful! We&apos;ll contact you soon.
                    </motion.div>
                  )}
                  {submitStatus === "error" && (
                    <motion.div
                      className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      Sorry, there was an error. Please try again.
                    </motion.div>
                  )}
                </motion.div>

                <motion.form
                  onSubmit={handleSubmit}
                  className="space-y-4 sm:space-y-6"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {/* School/Institute Name */}
                  <motion.div
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    <motion.input
                      type="text"
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleInputChange}
                      placeholder="School/Institute Name"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white text-black placeholder-gray-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      required
                      whileFocus={{
                        scale: 1.02,
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.div>

                  {/* Email Address */}
                  <motion.div
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    <motion.input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white text-black placeholder-gray-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      required
                      whileFocus={{
                        scale: 1.02,
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.div>

                  {/* Phone */}
                  <motion.div
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    <motion.input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white text-black placeholder-gray-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      required
                      whileFocus={{
                        scale: 1.02,
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.div>

                  {/* Address */}
                  <motion.div
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    <motion.input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Address"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white text-black placeholder-gray-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      required
                      whileFocus={{
                        scale: 1.02,
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.div>

                  {/* Comment */}
                  <motion.div
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    <motion.textarea
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      placeholder="Comment"
                      rows={3}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white text-black placeholder-gray-500 resize-none rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      whileFocus={{
                        scale: 1.02,
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.div>

                  {/* Sign Up Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-white text-black py-2 sm:py-3 px-4 sm:px-6 font-semibold rounded hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    variants={{
                      hidden: {
                        opacity: 0,
                        scale: 0.95,
                      },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                        },
                      },
                      hover: {
                        scale: 1.02,
                        transition: {
                          duration: 0.2,
                          ease: "easeInOut",
                        },
                      },
                      tap: {
                        scale: 0.98,
                        transition: {
                          duration: 0.1,
                        },
                      },
                    }}
                    whileHover={!isSubmitting ? "hover" : {}}
                    whileTap={!isSubmitting ? "tap" : {}}
                  >
                    {isSubmitting ? (
                      <motion.div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                        Submitting...
                      </motion.div>
                    ) : (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 1.2 }}
                      >
                        {config.buttonText}
                      </motion.span>
                    )}
                  </motion.button>
                </motion.form>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default StudentRegistrationSection;
